<template>
  <LinkWrapper v-bind="$props" @add-link="$emit('add-link', $event)">
    <LinkCard v-if="hasSetup">
      <LinkField>
        <Label block gutter-bottom>Form Destination</Label>
        <VText v-if="formType" component="span" class="source">
          <component :is="formType.iconComponent" />
          <span>{{ formType.name }}</span>
        </VText>
        <VText v-if="formType" component="span" class="source">
          <a :href="url" target="_blank" rel="noopener noreferrer" :title="url">
            <span>{{ formattedUrl }}</span>
            <ExternalLinkIcon />
          </a>
        </VText>
      </LinkField>

      <v-media-selector
        slot="media"
        v-model="mediaImage"
        :has-future-image="hasFutureImage"
        class="media-selector--post"
        :disabled="isReadonly || isStandardLayout"
        :loading="isSavingImage"
        @select="selectMedia"
        @remove="imageRemove"
      />

      <template slot="btm">
        <LinkBtn
          v-if="!isStandardLayout && !isStatsHidden"
          :active="isExpanded('stats')"
          :aria-label="`Link analytics - ${link.number_of_clicks} clicks`"
          :aria-controls="`stats-${id}`"
          @click="toggleExpand('stats')"
        >
          <LineChartIcon slot="icon" />
          <template v-if="link.number_of_clicks" slot="stat">
            {{ link.number_of_clicks | numberFormatter(1) }}
          </template>
        </LinkBtn>
        <LinkBtn
          v-if="!isDeleted && !isScheduleHidden"
          aria-label="Schedule link"
          :active="isExpanded('schedule')"
          :highlight="isExpanded('schedule')"
          :aria-controls="`schedule-${id}`"
          @click="toggleExpand('schedule')"
        >
          <CalendarIcon slot="icon" />
        </LinkBtn>
        <v-tooltip v-if="!isDeleted && !isPinnedHidden" top>
          <template #activator="{ on }">
            <LinkBtn
              v-if="!isDeleted"
              aria-label="Pin link"
              :pinned="pinned"
              :disabled="isPinnedDisabled"
              v-on="on"
              @click="pinned = !pinned"
            >
              <PinIcon slot="icon" />
            </LinkBtn>
          </template>
          <span>{{ pinnedText }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="
            !isStandardLayout && !isFreePlan && !isDeleted && !isEventTagHidden
          "
          top
        >
          <template #activator="{ on }">
            <LinkBtn
              aria-label="Event tag"
              :active="isExpanded('event-tag')"
              :aria-controls="`event-label-${id}`"
              v-on="on"
              @click="toggleExpand('event-tag')"
            >
              <TagIcon slot="icon" />
            </LinkBtn>
          </template>
          <span>Event tag</span>
        </v-tooltip>
        <v-tooltip v-if="isDeleted" top>
          <template #activator="{ on }">
            <LinkBtn
              class="link__btn--restore js-link-restore"
              aria-label="Restore link"
              :disabled="isRestoring || isFreePlan"
              v-on="on"
              @click="handleRestore"
            >
              <RepeatIcon slot="icon" />
            </LinkBtn>
          </template>
          <span>{{ isFreePlan ? 'Upgrade to restore' : 'Restore link' }}</span>
        </v-tooltip>
      </template>

      <template slot="actions">
        <StatsExpand
          :is-open="isExpanded('stats')"
          @close="toggleExpand('stats')"
        />
        <ScheduleExpand
          :is-open="isExpanded('schedule')"
          :link="link"
          @close="toggleExpand('schedule')"
        />
        <EventLabelExpand
          v-if="!isFreePlan"
          :is-open="isExpanded('event-tag')"
          :link="link"
          @close="toggleExpand('event-tag')"
        />
        <FormSettingsExpand
          :is-open="isExpanded('default')"
          :link-id="id"
          :form-options="options"
          :form-type="formType"
          @close="toggleExpand('default')"
          @save="saveOptions"
        />
      </template>
    </LinkCard>
    <FormSetup
      v-else
      :link-id="id"
      :form-options="options"
      :is-deleting="isDeleting"
      @setup-complete="toggleExpand('form')"
      @remove="handleDelete"
    />
  </LinkWrapper>
</template>

<script>
import {
  Card,
  CardText,
  CardActions,
  VText,
  Alert,
  InfoIcon,
  Button,
  Input,
  IconButton,
  VisibilityToggle,
  DragIcon,
  LineChartIcon,
  CalendarIcon,
  PinIcon,
  StarIcon,
  StarOIcon,
  RepeatIcon,
  ArchiveIcon,
  TrashIcon,
  ExternalLinkIcon,
  TagIcon,
  CircularLoader,
  FlexSpacer,
  Label,
  FORM_ICONS,
  List,
  ListItem,
  ListItemAction,
  ListItemTitle,
} from '@campsite-bio/component-lib';
import { computed } from 'vue';

import { LinkBtn, LinkWrapper, LinkCard, LinkField } from '../components';
import { StatsExpand, ScheduleExpand, EventLabelExpand } from '../expand';
import FormSetup from './form-setup';
import FormSettingsExpand from './settings-expand.vue';
import { InputUrl } from '../../form';
import { FORM_TYPES } from './form-types';
import { LinkProps } from '../link-props';
import { useLink } from '../compositions';

export default {
  components: {
    Card,
    CardText,
    CardActions,
    VText,
    Alert,
    InfoIcon,
    Button,
    Input,
    IconButton,
    DragIcon,
    LineChartIcon,
    CalendarIcon,
    PinIcon,
    StarIcon,
    StarOIcon,
    RepeatIcon,
    ArchiveIcon,
    TrashIcon,
    ExternalLinkIcon,
    TagIcon,
    StatsExpand,
    ScheduleExpand,
    EventLabelExpand,
    FormSettingsExpand,
    VisibilityToggle,
    LinkBtn,
    CircularLoader,
    FlexSpacer,
    InputUrl,
    Label,
    FormSetup,
    ...FORM_ICONS,
    List,
    ListItem,
    ListItemAction,
    ListItemTitle,
    LinkWrapper,
    LinkCard,
    LinkField,
  },

  props: LinkProps,

  setup(props) {
    const { link, isSavingOptions, options, hasSetup, saveOptions, ...rest } =
      useLink(props, {
        hasMedia: true,
        name: 'form link',
        optionsName: 'form',
        expandOnSetup: 'default',
      });

    const formType = computed(() => {
      if (!options.value.source) return null;
      return FORM_TYPES.find((type) => type.source === options.value.source);
    });

    const formattedUrl = computed(() => {
      if (!link.value.url) return null;

      const url = link.value.url.replace('https://', '');

      return url.length > 50 ? `${url.slice(0, 50)}...` : url;
    });

    const isStandardLayout = computed(() => {
      return link.value.form_options.layout === 'standard';
    });

    return {
      ...rest,
      options,
      formType,
      saveOptions,
      hasSetup,
      isSavingOptions,
      formattedUrl,
      isStandardLayout,
    };
  },
};
</script>

<style lang="scss" scoped>
.source,
.source > a {
  align-items: center;
  display: flex;
  word-break: break-all;

  > * {
    margin-right: 0.5rem;
  }
}
</style>
